import React, { useState } from 'react';
import { Wallet, Phone, Hash, ArrowDownLeft, ArrowUpRight, AlertCircle } from 'lucide-react';
import { checkAccount } from '../lib/cover';

// The last digit of an Imfuyo account number is a checking digit (the Luhn method), so a mistyped number is caught before anything is sent.
const accountNumberLooksRight = (n) => {
  if (!/^[1-9][0-9]{7}$/.test(n)) return false;
  let sum = 0, double = true;
  for (let i = 6; i >= 0; i--) { let d = Number(n[i]); if (double) { d *= 2; if (d > 9) d -= 9; } sum += d; double = !double; }
  return String((10 - (sum % 10)) % 10) === n[7];
};

// My Imfuyo account: a customer sees their balance and recent movements with their account number and registered phone.
// Money is received and held by Imfuyo's licensed payment partner; Imfuyo keeps the record.
const fmt = (v) => Number(v || 0).toLocaleString('en-KE');
const when = (d) => new Date(d + 'T00:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export default function MyAccount({ isDark }) {
  const [acct, setAcct] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setData(null);
    const digits = acct.replace(/\s+/g, '');
    if (!accountNumberLooksRight(digits)) { setError('That account number looks mistyped. It has 8 digits and is not your farmer number. Please check each digit.'); return; }
    setBusy(true);
    try { setData(await checkAccount(digits, phone.trim())); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const card = isDark ? 'bg-[#13211A] border-white/10 text-white' : 'bg-white border-[#DCE7DD] text-[#1B2B22]';
  const muted = isDark ? 'text-white/60' : 'text-[#5B6B61]';
  const input = `w-full rounded-xl border px-4 py-3 text-base outline-none focus:ring-2 focus:ring-[#285020] ${isDark ? 'bg-[#0E1812] border-white/15 text-white' : 'bg-white border-[#CFDCD2]'}`;

  return (
    <section className={`min-h-screen pt-28 pb-20 px-4 ${isDark ? 'bg-[#0B140F]' : 'bg-[#F4F8F4]'}`}>
      <div className="max-w-3xl mx-auto">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#BF4700] mb-3">My Imfuyo account</p>
        <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${isDark ? 'text-white' : 'text-[#14301A]'}`}>Check your balance and payments</h1>
        <p className={`text-lg mb-8 ${muted}`}>Enter your Imfuyo account number and the phone number you registered with us. Your account number has 8 digits and is on your plan or receipt. It is not your farmer number.</p>

        <form onSubmit={submit} className={`rounded-2xl border p-6 grid gap-4 sm:grid-cols-[1fr_1fr_auto] ${card}`}>
          <label className="block">
            <span className={`text-sm font-medium flex items-center gap-1.5 mb-1.5 ${muted}`}><Hash className="w-4 h-4" /> Account number</span>
            <input className={input} value={acct} onChange={(e) => setAcct(e.target.value)} placeholder="8-digit number" required autoComplete="off" inputMode="numeric" maxLength={9} />
          </label>
          <label className="block">
            <span className={`text-sm font-medium flex items-center gap-1.5 mb-1.5 ${muted}`}><Phone className="w-4 h-4" /> Registered phone</span>
            <input className={input} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07XX XXX XXX" required inputMode="tel" />
          </label>
          <button type="submit" disabled={busy} className="self-end rounded-full bg-[#285020] hover:bg-[#14301A] text-white font-semibold px-6 py-3 disabled:opacity-60">
            {busy ? 'Checking…' : 'Show my account'}
          </button>
        </form>

        {error && (
          <div className="mt-6 rounded-2xl border border-[#E9C9A8] bg-[#FBF1DE] text-[#7A4A0E] p-4 flex gap-3 items-start"><AlertCircle className="w-5 h-5 mt-0.5 shrink-0" /><p>{error}</p></div>
        )}

        {data && (
          <div className="mt-8 space-y-6">
            <div className="grid gap-4 sm:grid-cols-3">
              {[['Balance', data.balance, 'Kenya Shillings'], ['Money in', data.money_in, 'all time'], ['Money out', data.money_out, 'all time']].map(([k, v, s]) => (
                <div key={k} className={`rounded-2xl border p-5 text-center ${card}`}>
                  <p className={`text-xs font-semibold tracking-widest uppercase ${muted}`}>{k}</p>
                  <p className="text-3xl font-bold mt-1 text-[#285020]">{fmt(v)}</p>
                  <p className={`text-xs mt-1 ${muted}`}>{s}</p>
                </div>
              ))}
            </div>

            <div className={`rounded-2xl border p-6 ${card}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2"><Wallet className="w-5 h-5 text-[#285020]" /> {data.name}</h2>
                <span className={`text-sm ${muted}`}>Account {data.account_no} · {data.status}</span>
              </div>
              {data.entries.length === 0 ? <p className={muted}>No movements yet.</p> : (
                <ul className="divide-y divide-black/5 dark:divide-white/10">
                  {data.entries.map((e, i) => (
                    <li key={i} className="py-3 flex items-center gap-3">
                      {e.direction === 'in' ? <ArrowDownLeft className="w-5 h-5 text-[#285020] shrink-0" /> : <ArrowUpRight className="w-5 h-5 text-[#BF4700] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{e.kind}</p>
                        <p className={`text-sm ${muted}`}>{when(e.date)}{e.ref ? ` · ${e.ref}` : ''}</p>
                      </div>
                      <p className={`font-semibold tabular-nums ${e.direction === 'in' ? 'text-[#285020]' : ''}`}>{e.direction === 'in' ? '+ ' : '- '}{fmt(e.amount)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl bg-[#E9F2EC] text-[#14301A] p-6">
              <h3 className="font-bold text-lg mb-2">How to pay in</h3>
              <p>M-PESA Paybill <b>{data.pay_to.paybill}</b>, account number <b>{data.pay_to.account}</b>, name {data.pay_to.name}. Your payment shows on this page once Imfuyo has recorded it.</p>
            </div>
          </div>
        )}

        <p className={`mt-10 text-sm ${muted}`}>Your money is received and held by Imfuyo's licensed payment partner. Imfuyo keeps the record of every movement. Imfuyo is registered with Kenya's Office of the Data Protection Commissioner, certificate 151-193C-B376. Questions: call +254 110 563 185.</p>
      </div>
    </section>
  );
}
